import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, LinearProgress, Tooltip, Collapse, Button,
  ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery, useTheme, Alert,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getPlatformName, getPlatformColor } from '../config/platforms';
import { useAuth } from '../contexts/AuthContext';
import { SectionCard } from './SectionCard';
import { SectionSkeleton } from './SectionSkeleton';
import { formatMoney } from '../utils/money';

/** Local date, not UTC — toISOString() on a local midnight can land on the previous day. */
const iso = (d: Date) => {
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 10);
};

interface NetworkRow {
  platform: string;
  spend: number;
  impressions: number;
  reach: number;
  clicks: number;
  likes: number;
  posts: number;
}

/** One ad from an imported Campaign Manager report, as the import endpoint reports it back. */
interface ImportedAd {
  ad_id: string;
  campaign: string | null;
  from: string;
  to: string;
  days: number;
  spend: number;
  impressions: number;
  clicks: number;
  reactions: number;
  follows: number;
  matched: boolean;
  match_reason: string;
  post: { id: string; published_at: string; excerpt: string } | null;
  possible_posts?: { id: string; published_at: string }[];
  ad_text?: string;
}

interface PlacementRow {
  platform: string;
  placement: string;
  spend: number;
  impressions: number;
}

/**
 * Where the advertising money actually went, per network.
 *
 * **The network a post was boosted from is not where the money goes.** Meta delivers a boost
 * across its whole family, so an "Instagram post" boost routinely spends on Facebook as well —
 * one real ad here split £116 to Instagram and £3 to Facebook, and most of the Instagram share
 * went to Reels rather than the feed. Attributing spend to the post's own network would be
 * wrong, so this reads `publisher_platform` from the ad insights instead.
 *
 * Figures are lifetime-to-date per ad, not per day: Meta reports a running total, so the most
 * recent reading is the true one and summing days would multiply the spend.
 *
 * **LinkedIn boosts are imported, not fetched.** The app has no access to LinkedIn's
 * Advertising API, so an admin imports Campaign Manager's "Ad Performance" export here. The
 * server matches each ad to its post by text and adds it to this panel and the boosted figures.
 */
export const AdSpend: React.FC<{ days: number; onImported?: () => void }> = ({ days, onImported }) => {
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [imported, setImported] = useState<ImportedAd[] | null>(null);
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState<
    { networks: NetworkRow[]; placements: PlacementRow[]; total_spend: number; from?: string; to?: string } | null
  >(null);
  const [showPlacements, setShowPlacements] = useState(false);
  const [loading, setLoading] = useState(true);

  /**
   * Its own range, independent of the page's 7/30/90 toggle.
   *
   * A campaign runs to its own calendar — "what did the October boost cost" is a different
   * question from "how did the last 30 days go", and answering it should not mean changing
   * what the rest of the page shows. `null` follows the page.
   */
  const [range, setRange] = useState<{ from: Date; to: Date } | null>(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState<Date | null>(new Date(Date.now() - 30 * 86400000));
  const [draftTo, setDraftTo] = useState<Date | null>(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    const query = range
      ? `from=${iso(range.from)}&to=${iso(range.to)}`
      : `days=${days}`;
    try {
      const res = await fetch(`/api/analytics/ad-spend?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch {
      // A missing spend panel must not take the rest of Stats down with it.
    } finally {
      setLoading(false);
    }
  }, [days, range, token]);

  useEffect(() => { load(); }, [load]);

  const money = (n: number) => formatMoney(n);

  const importReport = async (file: File) => {
    setImporting(true);
    setImportError(null);
    setImported(null);
    try {
      // The file goes up as-is: Campaign Manager writes UTF-16, which the server decodes
      const res = await fetch('/api/analytics/import/linkedin-ads', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/octet-stream' },
        body: file,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setImportError(body.message || body.error || 'That file could not be imported.');
        return;
      }
      setImported(body.ads || []);
      load();
      onImported?.();
    } catch {
      setImportError('Network error while importing. Try again.');
    } finally {
      setImporting(false);
    }
  };

  // A custom range that found nothing still shows the panel, with its controls — hiding it
  // would look like the filter had broken rather than that the period was quiet.
  const empty = !data || !data.networks.length || data.total_spend <= 0;
  if (loading && !data) return <SectionSkeleton rows={2} />;
  // Admins still see the panel when there is no spend, so the import is always reachable
  if (empty && !range && !isAdmin) return null;

  const picker = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={range ? 'custom' : 'page'}
        onChange={(_, v) => {
          if (v === 'page') setRange(null);
          if (v === 'custom') setRangeOpen(true);
        }}
      >
        <ToggleButton value="page">Last {days} days</ToggleButton>
        <ToggleButton value="custom">
          {range ? `${iso(range.from)} → ${iso(range.to)}` : 'Date range'}
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  return (
    // The anchor lives on the card rather than a wrapper in the page, because this component
    // returns null when there is nothing to show and the jump nav checks the DOM for what it
    // can actually scroll to.
    <SectionCard
      id="ad-spend"
      title="Ad Spend By Network"
      actions={<>
        {isAdmin && (
          <Button
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => { setImportOpen(true); setImported(null); setImportError(null); }}
          >
            Import LinkedIn boost report
          </Button>
        )}
        {picker}
      </>}
      description={<>
        {empty
          ? 'No advertising spend recorded in this period.'
          : `${money(data!.total_spend)} ${range ? `from ${data!.from} to ${data!.to}` : `over the last ${days} days`}.`}
        {!empty && ' This is where the money actually went: boosting a post on Instagram or Facebook still spends across Meta\'s other networks. LinkedIn boosts appear once their Campaign Manager report is imported.'}
      </>}
    >
      <Dialog open={importOpen} onClose={() => !importing && setImportOpen(false)} fullScreen={narrow} maxWidth="sm" fullWidth>
        <DialogTitle>Import A LinkedIn Boost Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            LinkedIn does not let ShoutVector read boost results directly, so import the report from
            Campaign Manager instead. In Campaign Manager, open the boost&apos;s campaign, click{' '}
            <strong>Export</strong>, choose <strong>Ad Performance</strong> broken out <strong>by day</strong>, and
            choose that file here. Importing the same report again just updates it.
          </Typography>
          <Button variant="contained" component="label" disabled={importing} startIcon={<UploadFileIcon />}>
            {importing ? 'Importing…' : 'Choose report file'}
            <input
              hidden
              type="file"
              accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
              onChange={e => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) importReport(file);
              }}
            />
          </Button>
          {importing && <LinearProgress sx={{ mt: 2 }} />}
          {importError && <Alert severity="error" sx={{ mt: 2 }}>{importError}</Alert>}
          {imported && imported.map(ad => (
            <Alert key={ad.ad_id} severity={ad.matched ? 'success' : 'warning'} sx={{ mt: 2 }}>
              <strong>{money(ad.spend)}</strong> from {ad.from} to {ad.to} · {ad.impressions.toLocaleString()} impressions
              · {ad.clicks.toLocaleString()} clicks · {ad.reactions} reactions · {ad.follows} new followers
              <br />
              {ad.matched && ad.post
                ? <>Matched to the post &ldquo;{ad.post.excerpt}&hellip;&rdquo;</>
                : ad.match_reason === 'ambiguous'
                  ? <>More than one LinkedIn post has this text, so it was not tied to a post. Its spend still counts here.</>
                  : <>No published LinkedIn post matched &ldquo;{ad.ad_text}&hellip;&rdquo;. Its spend still counts here.</>}
            </Alert>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importing}>{imported ? 'Done' : 'Cancel'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rangeOpen} onClose={() => setRangeOpen(false)} fullScreen={narrow}>
        <DialogTitle>Ad Spend Over A Date Range</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: narrow ? 'column' : 'row', mt: 1 }}>
              <DatePicker label="From" value={draftFrom} onChange={setDraftFrom} />
              <DatePicker label="To" value={draftTo} onChange={setDraftTo} />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRange(null); setRangeOpen(false); }}>Clear</Button>
          <Button
            variant="contained"
            disabled={!draftFrom || !draftTo}
            onClick={() => {
              if (!draftFrom || !draftTo) return;
              // Accept either order rather than refusing — the intent is unambiguous.
              const [a, b] = draftFrom <= draftTo ? [draftFrom, draftTo] : [draftTo, draftFrom];
              setRange({ from: a, to: b });
              setRangeOpen(false);
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {!empty && data && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {data.networks.map(n => {
          const share = data.total_spend > 0 ? (n.spend / data.total_spend) * 100 : 0;
          const costPerLike = n.likes > 0 ? n.spend / n.likes : null;
          return (
            <Box key={n.platform}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90 }}>
                  {getPlatformName(n.platform)}
                </Typography>
                <Typography variant="body2">{money(n.spend)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {share.toFixed(0)}% · {n.impressions.toLocaleString()} impressions
                  {n.likes > 0 && ` · ${n.likes.toLocaleString()} likes`}
                  {costPerLike !== null && ` · ${money(costPerLike)} per like`}
                </Typography>
              </Box>
              <Tooltip arrow title={`${money(n.spend)} of ${money(data.total_spend)}`}>
                <LinearProgress
                  variant="determinate"
                  value={share}
                  sx={{
                    height: 6, borderRadius: 3, mt: 0.5,
                    '& .MuiLinearProgress-bar': { backgroundColor: getPlatformColor(n.platform) },
                  }}
                />
              </Tooltip>
            </Box>
          );
        })}
      </Box>
      )}

      {!empty && data && data.placements.length > 1 && (
        <>
          <Button size="small" sx={{ mt: 1.5 }} onClick={() => setShowPlacements(v => !v)}>
            {showPlacements ? 'Hide placements' : 'Show placements'}
          </Button>
          <Collapse in={showPlacements}>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {data.placements.filter(p => p.spend > 0).map(p => (
                <Typography key={`${p.platform}-${p.placement}`} variant="caption" color="text.secondary">
                  {getPlatformName(p.platform)} · {p.placement || 'unspecified'} — {money(p.spend)}
                  {' · '}{p.impressions.toLocaleString()} impressions
                </Typography>
              ))}
            </Box>
          </Collapse>
        </>
      )}
    </SectionCard>
  );
};
